import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Page, PageHeader, Card, Segmented, Label11, Chip, Loading, int, ago, shortDate } from "@/components/common";
import { Inline } from "@/lib/inline";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Check, X, CircleCheck, EyeOff } from "lucide-react";

// design_handoff_crewbench/screens/Shared Memory.dc.html: what every new session on a project starts with, the
// proposals waiting on the founder, and the versions they can roll back to.
const STATUS = { todo: "To do", "in progress": "In progress", review: "In review" };
const day = (iso) => shortDate(iso);

// what made a version (memory_routes: `change`, with who): rules edited, an entry approved (named by its proposer) or a
// rollback. Versions from before the notes say when they were replaced.
const CHANGE = {
  rules: (c) => `Rules edited by ${c.by_name || "a founder"}`,
  approved: (c) => `Approved an entry from ${c.by_name || "someone"}`,
  rollback: (c) => `Rolled back to v${c.to} by ${c.by_name || "a founder"}`,
};
function versionNote(v) {
  const c = v.change;
  if (c && CHANGE[c.kind]) return `${CHANGE[c.kind](c)} · ${day(c.at)}`;
  return v.current ? "In use by new sessions" : `Replaced ${day(v.snapshot_at)}`;
}

function SectionHead({ title, note, right, htmlFor }) {
  const Title = htmlFor ? "label" : "span";
  return (
    <div className="flex flex-wrap justify-between items-baseline gap-3">
      <div className="flex flex-col gap-0.5">
        <Title htmlFor={htmlFor} className="font-display text-[15px] tracking-[0.02em] font-semibold">{title}</Title>
        {note && <span className="text-sm text-muted-foreground">{note}</span>}
      </div>
      {right && <span className="text-sm text-muted-foreground">{right}</span>}
    </div>
  );
}

const ROWS = "flex flex-col gap-px bg-gr-hairline border-t";

export default function Memory() {
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const { data: projects } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const [pid, setPid] = useState(params.get("project") || "");
  useEffect(() => { if (!pid && projects?.projects?.length) setPid(projects.projects[0].id); }, [projects, pid]);
  const project = projects?.projects?.find((p) => p.id === pid);

  const { data, isLoading } = useQuery({ queryKey: ["memory", pid], enabled: !!pid, queryFn: async () => (await api.get(`/memory?project_id=${pid}`)).data });
  const { data: props } = useQuery({ queryKey: ["proposals"], queryFn: async () => (await api.get("/memory/proposals")).data, refetchInterval: 5000 });
  const { data: versions } = useQuery({ queryKey: ["versions", pid], enabled: !!pid, queryFn: async () => (await api.get(`/memory/versions?project_id=${pid}`)).data });
  const { data: team } = useQuery({ queryKey: ["team"], queryFn: async () => (await api.get("/team")).data });
  const names = Object.fromEntries((team?.members || []).map((m) => [m.id, m.name || m.email]));

  const saved = data?.memory?.rules || "";
  const [rules, setRules] = useState(null);  // null: no draft, the saved rules show
  useEffect(() => { setRules(null); }, [pid]);
  const draft = rules ?? saved;
  const dirty = draft !== saved;

  const refresh = () => ["memory", "versions", "proposals", "projects", "overview", "overview-counts"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const saveRules = useMutation({
    mutationFn: async () => api.put("/memory", { project_id: pid, rules: draft }),
    onSuccess: () => { toast.success(`Rules saved as version ${data.memory.version + 1}.`); setRules(null); refresh(); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const decide = useMutation({
    mutationFn: async ({ id, action }) => api.post(`/memory/proposals/${id}/${action}`),
    onSuccess: (_, { action }) => {
      toast.success(action === "approve" ? `Approved. New sessions on ${project?.name} start with it.` : "Rejected. It won't be added.");
      refresh();
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const rollback = useMutation({
    mutationFn: async (version) => (await api.post(`/memory/rollback?project_id=${pid}&version=${version}`)).data,
    onSuccess: (_, version) => { toast.success(`Rolled back to version ${version}. Saved as version ${data.memory.version + 1}.`); setRules(null); refresh(); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const pending = (props?.proposals || []).filter((p) => p.project_id === pid);
  const switcher = projects?.projects?.length > 0 && (
    <Segmented label="Project" options={projects.projects.map((p) => [p.id, p.name])} value={pid} onChange={setPid} testid="memory-project-select" />
  );
  if (!pid || isLoading || !data) return (
    <Page><PageHeader eyebrow="Shared memory" title="What every new session starts with." actions={switcher} />
      {projects && !projects.projects.length ? <Card className="p-5 text-muted-foreground">No projects yet. Each project has its own shared memory.</Card> : <Loading />}
    </Page>
  );

  const doc = data.memory;
  const knowledge = [...(doc.knowledge || [])].reverse();  // the newest first
  const history = [{ version: doc.version, current: true, change: doc.change },
                   ...(versions?.versions || []).filter((v) => v.version < doc.version)].slice(0, 6);
  return (
    <Page data-testid="owner-memory" className="[&_code]:bg-transparent [&_code]:px-0 [&_code]:text-[12px]">
      <PageHeader eyebrow={`Shared memory · Version ${doc.version}`} title={`What every new session on ${project?.name} starts with.`}
        status={pending.length === 0 ? "Nothing is waiting for you." : `${pending.length} ${pending.length === 1 ? "proposal is" : "proposals are"} waiting for you.`}
        actions={switcher} />
      <div className="flex flex-wrap gap-5 items-start">
        <div className="flex-[100_1_480px] min-w-0 flex flex-col gap-5">
          <Card className="px-5 py-[18px] flex flex-col gap-3">
            <SectionHead title="Objectives" note={<>Written from your open objectives and kept up to date. Change them on the <Link to="/founder/objectives" className="underline underline-offset-2 hover:opacity-50">Objectives page</Link>.</>} />
            {data.objectives.length ? (
              <div className={ROWS}>
                {data.objectives.map((o) => (
                  <div key={o.id} className="bg-card flex justify-between items-center gap-3 py-2">
                    <span>{o.title}</span><Label11 className="whitespace-nowrap">{STATUS[o.status] || o.status}</Label11>
                  </div>
                ))}
              </div>
            ) : <span className="text-muted-foreground">No open objectives. Sessions on this project link to General.</span>}
          </Card>

          <Card className="px-5 py-[18px] flex flex-col gap-3">
            <SectionHead title="Rules and conventions" note="You write these. Saving makes a new version you can roll back." htmlFor="mem-rules" />
            <Textarea id="mem-rules" rows={6} className="leading-5 px-2.5 py-2" value={draft} onChange={(e) => setRules(e.target.value)} data-testid="memory-rules" />
            <div className="flex flex-wrap items-center justify-end gap-3">
              {dirty ? (
                <button type="button" onClick={() => setRules(null)} className="text-sm text-muted-foreground underline underline-offset-2 hover:opacity-50">Discard changes</button>
              ) : <span className="text-sm text-muted-foreground">No changes to save.</span>}
              <Button aria-disabled={!dirty} disabled={saveRules.isPending} onClick={() => dirty && saveRules.mutate()} data-testid="memory-save-rules">Save rules</Button>
            </div>
          </Card>

          <Card className="px-5 py-[18px] flex flex-col gap-3">
            <SectionHead title="Project knowledge" note="Proposed from sessions and approved by you." right={knowledge.length === 1 ? "1 entry" : `${int(knowledge.length)} entries`} />
            {knowledge.length ? (
              <div className={ROWS}>
                {knowledge.map((k) => (
                  <div key={k.id} className="bg-card flex gap-2.5 py-2.5" data-testid="knowledge-item">
                    <CircleCheck className="w-3.5 h-3.5 mt-[3px] shrink-0 text-gr-success" />
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <span className="[text-wrap:pretty]"><Inline text={k.text} /></span>
                      <span className="text-sm text-muted-foreground">
                        {k.proposed_by ? `Proposed by ${k.proposed_by}` : `From session ${(k.from_session || "").slice(0, 6)}`} · approved {day(k.added_at)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : <span className="text-muted-foreground">Nothing approved yet.</span>}
          </Card>
        </div>

        <div className="flex-[1_0_300px] max-w-full flex flex-col gap-5">
          <Card className="px-5 py-[18px] flex flex-col gap-3">
            <SectionHead title="Waiting for you" right={pending.length ? String(pending.length) : ""} />
            <span className="flex items-start gap-2 text-sm text-muted-foreground">
              <EyeOff className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span className="[text-wrap:pretty]">A proposal is the only way anything from a session's chat reaches you. Nothing is added until you approve it.</span>
            </span>
            {pending.length ? pending.map((p) => (
              <div key={p.id} className="border rounded-lg p-3.5 flex flex-col gap-2.5" data-testid="proposal-item">
                <span className="text-gr-ink-secondary [text-wrap:pretty]"><Inline text={p.text} /></span>
                <span className="text-sm text-muted-foreground">Proposed by {names[p.coder_id] || "someone"} from session {(p.session_id || "").slice(0, 6)}, {ago(p.created_at)}</span>
                <div className="flex gap-2">
                  <Button className="flex-1" disabled={decide.isPending} onClick={() => decide.mutate({ id: p.id, action: "approve" })} data-testid={`proposal-approve-${p.id}`}><Check />Approve</Button>
                  <Button className="flex-1" variant="outline" disabled={decide.isPending} onClick={() => decide.mutate({ id: p.id, action: "reject" })} data-testid={`proposal-reject-${p.id}`}><X />Reject</Button>
                </div>
              </div>
            )) : <span className="text-muted-foreground">Nothing waiting. Proposals from sessions show up here.</span>}
          </Card>

          <Card className="px-5 py-[18px] flex flex-col gap-2">
            <span className="font-display text-[15px] tracking-[0.02em] font-semibold">Versions</span>
            <div className={ROWS}>
              {history.map((v) => (
                <div key={v.version} className="bg-card flex justify-between items-start gap-3 py-2.5">
                  <div className="flex flex-col min-w-0">
                    <span className="font-semibold tabular-nums">v{v.version}</span>
                    <span className="text-sm text-muted-foreground [text-wrap:pretty]">{versionNote(v)}</span>
                  </div>
                  {v.current ? <Chip className="py-0 leading-[18px] whitespace-nowrap">Current</Chip> : (
                    <button type="button" onClick={() => rollback.mutate(v.version)} disabled={rollback.isPending}
                      className="text-sm underline underline-offset-2 whitespace-nowrap hover:opacity-50" data-testid={`rollback-${v.version}`}>Roll back</button>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </Page>
  );
}
